<template>
  <div class="c-preset-manager">
    <GuDialog v-model:open="isPresetsDialogOpen">
      <GuDialogTrigger as-child>
        <GuButton
          variant="primary"
          size="sm"
          icon="bx bx-filter"
          :is-marked="inSelectedMode"
        >
          {{ inSelectedMode && selectedPreset?.name ? selectedPreset.name : 'Filter Presets' }}
        </GuButton>
      </GuDialogTrigger>

      <GuDialogContent centered>
        <template #title>
          <h5 class="modal-title fs-18">Filter Presets</h5>
        </template>

        <div class="d-flex">
          <GuSelect
            v-model="selectedPresetId"
            class="preset-select"
            aria-label="Selected Preset"
            :disabled="loading"
            placeholder="No preset selected"
            @update:model-value="setPreset"
          >
            <GuSelectGroup
              v-for="levelValue in PRESET_SHARE_LEVEL"
              :key="levelValue"
              :label="levelValue"
            >
              <GuSelectItem
                v-if="presetsByType(levelValue).length === 0"
                disabled
                :value="`${levelValue}-disabled`"
              >
                No {{ levelValue.toLowerCase() }} presets
              </GuSelectItem>
              <GuSelectItem
                v-for="preset in presetsByType(levelValue)"
                :key="preset.id"
                :value="preset.id"
              >
                <i
                  v-if="preset.id === favoritePresetId"
                  class="bx bxs-star order-1 favorite"
                  aria-hidden="true"
                />

                {{ preset.name }}
              </GuSelectItem>
            </GuSelectGroup>
          </GuSelect>

          <GuButton
            v-if="inSelectedMode"
            :icon="isSelectedPresetFavorite ? 'bxs-star' : 'bx-star'"
            :class="`border-0 ms-4 ${isSelectedPresetFavorite ? 'favorite' : '' }`"
            outline
            @click.prevent="handleFavoritePreset"
          />
        </div>

        <template #footer>
          <GuButton
            v-if="inSelectedMode || inEditMode || inCopyMode"
            outline
            size="sm"
            class="border-0 me-auto"
            aria-label="Clear Selected Preset"
            :disabled="loading"
            @click="clear"
          >
            Clear Selection
          </GuButton>

          <GuButton
            v-if="inSelectedMode && userCanEditPreset"
            icon="bxs-edit"
            outline
            size="sm"
            class="border-0"
            aria-label="Edit Preset"
            :disabled="loading || inEditMode"
            @click="startEditing"
          >
            Edit
          </GuButton>

          <GuButton
            v-if="inSelectedMode"
            icon="bxs-copy"
            outline
            size="sm"
            class="border-0"
            aria-label="Copy Preset"
            :disabled="loading"
            @click="copy"
          >
            Duplicate
          </GuButton>

          <GuButton
            v-if="inNoneMode"
            icon="bxs-plus-square"
            size="sm"
            class="border-0"
            aria-label="Create Preset"
            variant="success"
            :disabled="loading || (inCreateMode && (!newShareLevel || !newPresetName))"
            @click="initializeNewPreset"
          >
            Create Preset
          </GuButton>
        </template>
      </GuDialogContent>
    </GuDialog>

    <GuDrawer
      v-model:open="isSideDrawerOpen"
      @update:open="(drawerIsOpen) => !drawerIsOpen && closeSideDrawer()"
    >
      <GuDrawerContent side="left" static-backdrop>
        <template #title>
          <h2 class="offcanvas-title">{{ sideDrawerHeaderText }}</h2>
        </template>

        <div v-if="inCreateMode || inEditMode || inCopyMode" class="d-flex flex-column gap-4">
          <div v-if="inEditMode" class="form-group">
            <GuLabel class="fw-bold m-1" for="presetIdInput">
              Preset ID
            </GuLabel>
            <GuInput
              id="presetIdInput"
              v-model="selectedPresetId"
              class="preset-input-name mr-1"
              disabled
            />
          </div>

          <div class="form-group">
            <GuLabel class="fw-bold m-1" for="presetNameInput">
              Preset Name
            </GuLabel>
            <GuInput
              id="presetNameInput"
              v-model="newPresetName"
              class="preset-input-name mr-1"
              required
              :disabled="loading"
            />
          </div>

          <div class="form-group">
            <GuLabel class="fw-bold m-1" for="presetShareLevelSelect">
              Share Level
            </GuLabel>
            <GuSelect
              id="presetShareLevelSelect"
              v-model="newShareLevel"
              class="preset-share-level"
              :disabled="loading"
            >
              <GuSelectItem
                v-for="shareLevel in allowedCreateEditPresetShareLevels"
                :key="shareLevel"
                :value="shareLevel"
              >
                {{ shareLevel }}
              </GuSelectItem>
            </GuSelect>
          </div>

          <div class="scope-checkboxes form-group mt-1">
            <GuLabel class="fw-bold m-1" for="presetSiteScopeCheckboxes">
              Site Scope
            </GuLabel>

            <div class="checkbox-group d-flex flex-column gap-2">
              <div v-for="siteScope in normalizedSiteScopes" :key="siteScope.value" class="form-check form-check-inline">
                <GuCheckbox
                  :id="`site-scope-${siteScope.value}`"
                  :checked="siteScopeChecked[siteScope.value]"
                  class="as-field-boolean-checkbox"
                  :aria-label="`Select site scope ${siteScope.label}`"
                  :disabled="siteScope.value === scope || normalizedSiteScopes.length <= 1"
                  @update:checked="updateSiteScopes(siteScope.value, $event)"
                />
                <GuLabel :for="`site-scope-${siteScope.value}`">{{ siteScope.label }}</GuLabel>
              </div>
            </div>
          </div>

          <div v-if="newShareLevel === PRESET_SHARE_LEVEL.ROLE" class="roles-checkboxes form-group">
            <GuLabel class="h6 m-1" for="presetRoleCheckboxes">
              Roles
            </GuLabel>
            <div class="checkbox-group d-flex flex-column gap-2">
              <div v-for="role in roles" :key="role" class="form-check form-check-inline">
                <GuCheckbox
                  :id="`role-${role}`"
                  v-model:checked="roleChecked[role]"
                  class="as-field-boolean-checkbox mt-0"
                  :aria-label="`Select role ${role}`"
                  @update:checked="updateRoles(role, $event)"
                />
                <GuLabel :for="`role-${role}`">{{ role }}</GuLabel>
              </div>
            </div>
          </div>

          <div class="preset-buttons-container d-flex justify-content-end gap-2">
            <GuButton
              v-if="inCreateMode || inCopyMode"
              icon="bxs-check-square"
              size="sm"
              :disabled="!newPresetName || !newShareLevel"
              :loading="loading"
              @click="create"
            >
              Create
            </GuButton>

            <GuButton
              v-if="inEditMode && userCanEditPreset"
              size="sm"
              variant="danger"
              @click="isDeleteDialogOpen = true"
            >
              Delete
            </GuButton>

            <GuButton
              v-if="inEditMode && userCanEditPreset"
              icon="bxs-save"
              size="sm"
              variant="success"
              :disabled="!saveAllowed"
              :title="!saveAllowed ? 'No changes to save. Change the preset name, share level, scopes, roles, or grid filters/columns to enable Save.' : undefined"
              @click="update"
            >
              Save
            </GuButton>

            <GuButton
              v-if="inEditMode"
              variant="info"
              size="sm"
              icon="bx-copy"
              :disabled="loading"
              @click="copy"
            >
              Duplicate
            </GuButton>

            <GuButton
              v-if="inCreateMode || inCopyMode || inEditMode"
              size="sm"
              icon="bx-x"
              variant="secondary"
              :disabled="loading"
              @click="closeSideDrawer"
            >
              Cancel
            </GuButton>
          </div>
        </div>
      </GuDrawerContent>
    </GuDrawer>

    <GuDialog v-model:open="isDeleteDialogOpen">
      <GuDialogContent>
        <template #title>
          <h5 class="modal-title fs-18">Confirm to Delete</h5>
        </template>

        <p>Are you sure you want to delete this preset?</p>

        <template #footer>
          <GuDialogClose as-child>
            <GuButton variant="secondary">
              Cancel
            </GuButton>
          </GuDialogClose>

          <GuButton variant="danger" @click="handleDeletePreset">
            Delete
          </GuButton>
        </template>
      </GuDialogContent>
    </GuDialog>
  </div>
</template>

<script setup>
import { computed, provide } from 'vue'
import { GuButton } from '@shared/gu-components/button'
import { GuDialog, GuDialogClose, GuDialogContent, GuDialogTrigger } from '@shared/gu-components/dialog'
import { GuInput } from '@shared/gu-components/input'
import { GuLabel } from '@shared/gu-components/label'
import { GuSelect, GuSelectItem, GuSelectGroup } from '@shared/gu-components/select'
import { GuDrawer, GuDrawerContent } from '@shared/gu-components/drawer'
import { GuCheckbox } from '@shared/gu-components/checkbox'
import { useFilterPresets } from '@gu-datagrid-new/composables/FilterPresets'

const props = defineProps({
  admin: {
    type: Boolean,
    default: false,
    description: 'Whether the user is an administrator (to create private/public presets)',
  },
  gridApi: {
    type: Object,
    required: true,
  },
  gridChangedFromPreset: {
    type: Boolean,
    default: false,
    description: 'Indicates whether grid state differs from provided preset',
  },
  pageLoadPresetId: {
    type: Number,
    default: null,
    description: 'Page load preset id',
  },
  presetIdParamKey: {
    type: String,
    default: 'viewId',
    description: 'URL param key for loading preset filter on page mount',
  },
  roles: {
    type: Array,
    default: () => [],
    description: 'List of assignable roles',
  },
  siteScopes: {
    type: Array,
    default: () => [],
    description: 'Grid backend identifiers',
  },
  scope: {
    type: String,
    default: null,
    description: 'Grid backend identifier',
  },
})

const emits = defineEmits([
  'clearPreset',
  'presetCreated',
  'presetDeleted',
  'presetsLoaded',
  'setPreset',
])

const {
  PRESET_SHARE_LEVEL,
  selectedPresetId,
  selectedPreset,
  newPresetName,
  newShareLevel,
  newPresetSiteScopes,
  newPresetRoles,
  // presets,
  loading,
  sideDrawerHeaderText,
  isSideDrawerOpen,
  isDeleteDialogOpen,
  isPresetsDialogOpen,
  allowedCreateEditPresetShareLevels,
  userCanEditPreset,
  saveAllowed,
  inCopyMode,
  inCreateMode,
  inEditMode,
  inNoneMode,
  inSelectedMode,
  presetsByType,
  clear,
  initializeNewPreset,
  copy,
  create,
  handleDeletePreset,
  startEditing,
  update,
  closeSideDrawer,
  setPreset,
  favoritePresetId,
  deleteFavoritePreset,
  setFavoritePreset,
} = useFilterPresets(props, emits)

provide('presetState', {
  selectedPresetId,
  selectedPreset,
})

defineExpose({
  clear,
})

const SITE_SCOPES_DICTIONARY = {
  'config.ic-applications-presets': 'My Projects',
  'config.dashboard-outstanding-actions': 'Dashboard Outstanding Actions',
  'config.dashboard.charts.presets': 'Dashboard Charts',
}

const normalizedSiteScopes = computed(() => {
  return props.siteScopes.map(scope => {
    // If scope is already an object with label and value, return as is
    if (typeof scope === 'object' && scope.label && scope.value) return scope

    // If scope is a string, transform it to object format
    if (typeof scope === 'string') {
      return {
        label: SITE_SCOPES_DICTIONARY[scope] || scope,
        value: scope,
      }
    }

    return scope
  })
})

const isSelectedPresetFavorite = computed(() => favoritePresetId.value === selectedPresetId.value)

const siteScopeChecked = computed(() => {
  const checked = {}
  normalizedSiteScopes.value.forEach(scope => {
    checked[scope.value] = newPresetSiteScopes.value.includes(scope.value)
  })
  return checked
})

const roleChecked = computed(() => {
  const checked = {}
  props.roles.forEach(role => {
    checked[role] = newPresetRoles.value.includes(role)
  })
  return checked
})

const updateSiteScopes = (scope, isChecked) => {
  if (isChecked) {
    newPresetSiteScopes.value = [...newPresetSiteScopes.value, scope]
  } else {
    newPresetSiteScopes.value = newPresetSiteScopes.value.filter(s => s !== scope)
  }
}

const updateRoles = (role, isChecked) => {
  if (isChecked) {
    newPresetRoles.value = [...newPresetRoles.value, role]
  } else {
    newPresetRoles.value = newPresetRoles.value.filter(r => r !== role)
  }
}

const handleFavoritePreset = () => {
  if (favoritePresetId.value) deleteFavoritePreset(isSelectedPresetFavorite.value ? null : selectedPresetId.value)
  else setFavoritePreset(selectedPresetId.value)
}
</script>

<style lang="scss" scoped>
.c-preset-manager {
  display: flex;
  flex-direction: row;
  align-items: center;

  .preset-actions-container {
    display: flex;
  }

  .preset-buttons-container {
    display: flex;
    justify-content: flex-end;

    i {
      margin-right: .25rem;
    }

    button {
      margin-left: .75rem;
      padding-left: .5rem;
    }
  }

  select {
    &.preset-select {
      width: calc(100% + 0.4rem);
    }

    &.form-control:not([multiple]) {
      background-position: right 0.3rem center;
    }
  }

  .checkbox-group {
    padding-left: 1rem;
  }
}

i.bx.bxs-star {
  color: #FFD700;
}

.favorite {
  ::v-deep i.bx.bxs-star {
    color: #FFD700;
  }
}
</style>
